/**
 * Tela 3D do CCO: sala de controle holográfica (opção da tela nova) — Nexora Grid (GUS-1505).
 *
 * Criado em: 18/10/2026 · Última modificação: 18/10/2026 · Versão: 2.0.0
 *
 * Protótipo aprovado v4, sobre o motor do SCADA 3D (three.js), com outra imersão:
 * - chão em grade e "poeira de dados" subindo. Cada cidade é um núcleo com anel de luz. Os setores com anomalia ficam
 *   **na frente**, com feixe e pulso no chão na cor do nível. Os normais correm num **halo por trás** da cidade e pausam
 *   quando o mouse está sobre um card da cidade;
 * - os cards são os mesmos da tela Clássica (`Cards.tsx`, dados ao vivo), colocados pelo CSS3DRenderer e sempre de
 *   frente para a câmera, para o texto nunca esticar;
 * - há duas camadas CSS3D: o halo fica atrás e a cidade, as anomalias e o que está em foco ficam na frente. O navegador
 *   decide o clique pela ordem das camadas, então nada em movimento cobre o card da cidade;
 * - níveis (v2.0, GUS-1504), como no protótipo:
 *   - cidade: as outras saem; os blocos ficam acima, os setores à frente em grade de leitura e as tendências abaixo;
 *   - setor: ele avança e os equipamentos se abrem ligados por linhas de luz;
 *   - equipamento: o painel abre ao lado;
 *   - diagnóstico: abre abaixo.
 *   Não há manobra na 3D: ela fica só na tela Clássica;
 * - câmera orbital: arrastar gira, roda fora dos cards aproxima e afasta, setas e ＋ − ⟲.
 * A navegação entre níveis (clique, Enter, roda sobre o card, Esc) é da tela que a contém (`CcoNovo.tsx`).
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import * as THREE from "three";
import { CSS3DObject, CSS3DRenderer } from "three/examples/jsm/renderers/CSS3DRenderer.js";
import { anomalo, type Nivel } from "./anomalia";
import { CardCidade, CardEquip, CardSetor, CorpoDiag, Hud, PainelEquip, blocosCidade, blocosTendencia, useTendencias } from "./Cards";
import type { Modelo } from "./dados";

const HALO_R = 390, HALO_INI = 14, HALO_FIM = 166, HALO_PASSO = 31;
const ESPACO = 840;
const RAIO_MIN = 320, RAIO_MAX = 4600, FI_MIN = 0.35, FI_MAX = 1.5;
const ease = (t: number) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);

type Tipo = "cidade" | "fila" | "halo" | "bloco" | "tend" | "equip" | "painel" | "diag";
type Slot = {
  chave: string; tipo: Tipo; cidade: string; ref: string; copia: boolean; k: number; el: HTMLDivElement; obj: CSS3DObject;
  escala: number; alvoEscala: number; opacidade: number; alvoOpacidade: number; fatorHalo: number; fixo: THREE.Vector3 | null; entrada: number;
};
type Nucleo = { codigo: string; i: number; centro: THREE.Vector3; grupo: THREE.Group; anel: THREE.Mesh; pulsos: THREE.Mesh[]; feixes: THREE.Object3D[]; giro: number; comprimento: number };
type Motor = {
  gl: THREE.WebGLRenderer; cssFundo: CSS3DRenderer; cssFrente: CSS3DRenderer; cena: THREE.Scene; cenaFundo: THREE.Scene; cenaFrente: THREE.Scene;
  camera: THREE.PerspectiveCamera; pos: THREE.Vector3; alvo: THREE.Vector3; sph: THREE.Spherical; sphAlvo: THREE.Spherical;
  voo: { t: number; dur: number; dePos: THREE.Vector3; deAlvo: THREE.Vector3; paraPos: THREE.Vector3; paraAlvo: THREE.Vector3 } | null;
  vista: { pos: THREE.Vector3; alvo: THREE.Vector3 } | null; slots: Slot[]; extras: Slot[]; linhas: THREE.Object3D[];
  nucleos: Nucleo[]; poeira: THREE.Points; tempo: number; pausaHalo: Set<string>; arrastou: boolean;
};

function corToken(nome: string, reserva: string): THREE.Color {
  const v = getComputedStyle(document.documentElement).getPropertyValue(nome).trim();
  return new THREE.Color(v || reserva);
}
const coresNivel = (): Record<Nivel, THREE.Color> => ({
  normal: corToken("--marca-ciano", "#40bee6"), atencao: corToken("--alarme-medio", "#f4c430"), alto: corToken("--alarme-alto", "#f28c28"),
  critico: corToken("--alarme-critico", "#e5383b"), sem_comunicacao: corToken("--estado-sem-comunicacao", "#b16cf0"),
});
const pontoHalo = (c: THREE.Vector3, graus: number) => {
  const a = (graus * Math.PI) / 180;
  return new THREE.Vector3(c.x + HALO_R * Math.cos(a), c.y + 20, c.z - 40 - HALO_R * 0.62 * Math.sin(a));
};
const centroCidade = (i: number, n: number) => new THREE.Vector3((i - (n - 1) / 2) * ESPACO, 0, n > 2 && i === Math.floor(n / 2) ? -160 : 0);
const V = (x: number, y: number, z: number) => new THREE.Vector3(x, y, z);

function liberar(obj: THREE.Object3D) {
  obj.traverse((o) => {
    const m = o as THREE.Mesh;
    m.geometry?.dispose();
    const mat = m.material as THREE.Material | THREE.Material[] | undefined;
    (Array.isArray(mat) ? mat : mat ? [mat] : []).forEach((x) => x.dispose());
  });
}

export type Props3d = { modelo: Modelo; nivel: number; cidade?: string; setor?: string; equip?: string; congelado: boolean; reduzido: boolean };

export function Cco3d({ modelo, nivel, cidade, setor, equip, congelado, reduzido }: Props3d) {
  const palco = useRef<HTMLDivElement>(null);
  const motor = useRef<Motor | null>(null);
  const [slots, setSlots] = useState<Slot[]>([]);
  const [extras, setExtras] = useState<Slot[]>([]);
  const estado = useRef({ nivel, cidade, setor, congelado, reduzido });
  estado.current = { nivel, cidade, setor, congelado, reduzido };
  const modeloRef = useRef(modelo);
  modeloRef.current = modelo;
  const tendencias = useTendencias(nivel >= 1 ? cidade : undefined);

  // ------------------------------------------------------------------ motor
  useLayoutEffect(() => {
    const el = palco.current!;
    const w = el.clientWidth, h = el.clientHeight;
    const gl = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    gl.setPixelRatio(Math.min(2, window.devicePixelRatio));
    gl.setSize(w, h);
    gl.domElement.className = "gl-3d";
    const cssFundo = new CSS3DRenderer(), cssFrente = new CSS3DRenderer();
    cssFundo.setSize(w, h); cssFrente.setSize(w, h);
    cssFundo.domElement.className = "css3d camada-fundo";
    cssFrente.domElement.className = "css3d camada-frente";
    el.prepend(gl.domElement, cssFundo.domElement, cssFrente.domElement);
    const cena = new THREE.Scene();
    cena.fog = new THREE.Fog(corToken("--marca-marinho", "#050c14"), 2200, 6200);
    const camera = new THREE.PerspectiveCamera(40, w / h, 1, 14000);
    const ciano = corToken("--marca-ciano", "#40bee6");
    const grade = new THREE.GridHelper(9000, 90, ciano, ciano.clone().multiplyScalar(0.35));
    grade.position.y = -340;
    (grade.material as THREE.Material).transparent = true;
    (grade.material as THREE.Material).opacity = 0.22;
    cena.add(grade);
    const n = 1400, pts = new Float32Array(n * 3);
    for (let i = 0; i < n; i++) { pts[i * 3] = (Math.random() - 0.5) * 8400; pts[i * 3 + 1] = -340 + Math.random() * 1740; pts[i * 3 + 2] = -3000 + Math.random() * 4600; }
    const geo = new THREE.BufferGeometry();
    geo.setAttribute("position", new THREE.BufferAttribute(pts, 3));
    const poeira = new THREE.Points(geo, new THREE.PointsMaterial({ color: ciano, size: 3, transparent: true, opacity: 0.45, depthWrite: false }));
    cena.add(poeira);
    const pos = V(0, 230, 1450), alvo = V(0, -60, -100);
    const sph = new THREE.Spherical().setFromVector3(pos.clone().sub(alvo));
    const m: Motor = { gl, cssFundo, cssFrente, cena, cenaFundo: new THREE.Scene(), cenaFrente: new THREE.Scene(), camera, pos, alvo, sph, sphAlvo: sph.clone(),
      voo: null, vista: null, slots: [], extras: [], linhas: [], nucleos: [], poeira, tempo: 0, pausaHalo: new Set(), arrastou: false };
    motor.current = m;

    let antes = performance.now();
    let quadro = requestAnimationFrame(function passo(agora) {
      const dt = Math.min(0.05, (agora - antes) / 1000);
      antes = agora;
      const st = estado.current;
      m.tempo += dt;
      if (m.voo) {
        const v = m.voo;
        v.t = Math.min(1, v.t + dt / v.dur);
        m.pos.lerpVectors(v.dePos, v.paraPos, ease(v.t));
        m.alvo.lerpVectors(v.deAlvo, v.paraAlvo, ease(v.t));
        if (v.t >= 1) { m.voo = null; m.sph.setFromVector3(m.pos.clone().sub(m.alvo)); m.sphAlvo.copy(m.sph); }
      } else {
        const k = st.reduzido ? 1 : Math.min(1, dt * 8);
        m.sph.radius += (m.sphAlvo.radius - m.sph.radius) * k;
        m.sph.theta += (m.sphAlvo.theta - m.sph.theta) * k;
        m.sph.phi += (m.sphAlvo.phi - m.sph.phi) * k;
        m.pos.copy(m.alvo).add(new THREE.Vector3().setFromSpherical(m.sph));
      }
      camera.position.copy(m.pos);
      camera.lookAt(m.alvo);
      for (const nu of m.nucleos) {
        if (!st.congelado && !st.reduzido && !m.pausaHalo.has(nu.codigo) && !(st.nivel >= 2 && st.cidade === nu.codigo)) nu.giro += dt * 7;
        for (const p of nu.pulsos) {
          const t = (m.tempo * 0.8 + p.position.x * 0.001) % 1;
          p.scale.setScalar(1 + t * 3);
          (p.material as THREE.MeshBasicMaterial).opacity = 0.8 * (1 - t);
        }
        (nu.anel.material as THREE.MeshBasicMaterial).opacity = 0.55 + 0.3 * Math.sin(m.tempo * 2 + nu.i);
      }
      if (!st.reduzido) {
        const arr = poeira.geometry.attributes.position as THREE.BufferAttribute;
        const a = arr.array as Float32Array;
        for (let i = 1; i < a.length; i += 3) { a[i] += dt * 14; if (a[i] > 1400) a[i] = -340; }
        arr.needsUpdate = true;
      }
      for (const s of [...m.slots, ...m.extras]) {
        if (s.tipo === "halo" && !s.fixo) {
          const nu = m.nucleos.find((x) => x.codigo === s.cidade);
          if (nu) {
            const g = HALO_INI + ((((s.k * HALO_PASSO + nu.giro) % nu.comprimento) + nu.comprimento) % nu.comprimento);
            s.obj.position.copy(pontoHalo(nu.centro, g));
            s.fatorHalo = g > HALO_FIM ? 0 : g > HALO_FIM - 14 ? (HALO_FIM - g) / 14 : g < HALO_INI + 14 ? (g - HALO_INI) / 14 : 1;
          }
        }
        if (s.fixo) { s.obj.position.lerp(s.fixo, st.reduzido ? 1 : Math.min(1, dt * 4)); s.fatorHalo = 1; }
        s.obj.quaternion.copy(camera.quaternion);
        s.escala += (s.alvoEscala - s.escala) * Math.min(1, dt * 10);
        s.opacidade += (s.alvoOpacidade - s.opacidade) * (st.reduzido ? 1 : Math.min(1, dt * 6));
        if (s.entrada < 1) s.entrada = st.reduzido ? 1 : Math.min(1, s.entrada + dt * 2.2);
        s.obj.scale.setScalar(s.escala * Math.max(0.01, s.entrada));
        const op = s.opacidade * s.fatorHalo * (s.entrada < 0 ? 0 : 1);
        s.el.style.opacity = op.toFixed(3);
        s.el.style.pointerEvents = op < 0.5 ? "none" : "auto";
        s.el.style.visibility = op < 0.02 ? "hidden" : "visible";
      }
      gl.render(cena, camera);
      cssFundo.render(m.cenaFundo, camera);
      cssFrente.render(m.cenaFrente, camera);
      quadro = requestAnimationFrame(passo);
    });
    const obs = new ResizeObserver(() => {
      const W = el.clientWidth, H = el.clientHeight;
      if (!W || !H) return;
      camera.aspect = W / H; camera.updateProjectionMatrix(); gl.setSize(W, H); cssFundo.setSize(W, H); cssFrente.setSize(W, H);
    });
    obs.observe(el);
    return () => {
      cancelAnimationFrame(quadro);
      obs.disconnect();
      liberar(cena);
      gl.dispose();
      gl.domElement.remove(); cssFundo.domElement.remove(); cssFrente.domElement.remove();
      motor.current = null;
    };
  }, []);

  const voar = useCallback((paraPos: THREE.Vector3, paraAlvo: THREE.Vector3, dur = 1.3) => {
    const m = motor.current;
    if (!m) return;
    m.vista = { pos: paraPos.clone(), alvo: paraAlvo.clone() };
    m.voo = { t: 0, dur: estado.current.reduzido ? 0.01 : dur, dePos: m.pos.clone(), deAlvo: m.alvo.clone(), paraPos, paraAlvo };
  }, []);
  const vistaGeral = useCallback(() => {
    const m = motor.current!;
    const largura = (Math.max(1, m.nucleos.length) - 1) * ESPACO + 1100;
    const meia = Math.tan(THREE.MathUtils.degToRad(20)) * m.camera.aspect;
    return { pos: V(0, 230, Math.max(1450, largura / (2 * meia) + 120) - 100), alvo: V(0, -60, -100) };
  }, []);

  const novoSlot = (m: Motor, chave: string, tipo: Tipo, cidadeCodigo: string, posicao: THREE.Vector3, ref = "", copia = false, k = 0): Slot => {
    const el = document.createElement("div");
    el.className = `slot-3d slot-${tipo}`;
    const obj = new CSS3DObject(el);
    obj.position.copy(posicao);
    (tipo === "halo" ? m.cenaFundo : m.cenaFrente).add(obj);
    const s: Slot = { chave, tipo, cidade: cidadeCodigo, ref, copia, k, el, obj, escala: 1, alvoEscala: 1, opacidade: 0, alvoOpacidade: 1, fatorHalo: 1, fixo: null, entrada: 1 };
    if (tipo === "cidade" || tipo === "fila" || tipo === "halo" || tipo === "equip") {
      el.addEventListener("pointerenter", () => { s.alvoEscala = estado.current.nivel === 0 ? 1.45 : 1.12; m.pausaHalo.add(cidadeCodigo); });
      el.addEventListener("pointerleave", () => { s.alvoEscala = 1; window.setTimeout(() => m.pausaHalo.delete(cidadeCodigo), 1500); });
    }
    return s;
  };

  // ------------------------------------------------------------------ cidades (refeitas quando a composição muda)
  const assinatura = modelo.cidades.map((c) => `${c.municipio.codigo}:${c.nota.nivel}:` +
    c.setores.map((s) => `${s.codigo}${anomalo(s.nota) ? `!${s.nota.nivel}` : ""}`).join(",")).join(";");
  useEffect(() => {
    const m = motor.current;
    if (!m) return;
    m.slots.forEach((s) => { m.cenaFundo.remove(s.obj); m.cenaFrente.remove(s.obj); s.el.remove(); });
    m.nucleos.forEach((nu) => { m.cena.remove(nu.grupo); liberar(nu.grupo); });
    m.slots = []; m.nucleos = [];
    const cores = coresNivel(), ciano = cores.normal;
    const cidades = modeloRef.current.cidades;
    const novos: Slot[] = [];
    cidades.forEach((c, i) => {
      const centro = centroCidade(i, cidades.length);
      const grupo = new THREE.Group();
      const anel = new THREE.Mesh(new THREE.RingGeometry(200, 214, 72),
        new THREE.MeshBasicMaterial({ color: c.nota.nivel === "normal" ? ciano : cores[c.nota.nivel], transparent: true, opacity: 0.85, side: THREE.DoubleSide }));
      anel.rotation.x = -Math.PI / 2;
      anel.position.set(centro.x, -338, centro.z);
      grupo.add(anel);
      const disco = new THREE.Mesh(new THREE.CircleGeometry(200, 72), new THREE.MeshBasicMaterial({ color: ciano, transparent: true, opacity: 0.06, side: THREE.DoubleSide }));
      disco.rotation.x = -Math.PI / 2;
      disco.position.copy(anel.position);
      grupo.add(disco);
      const arco: THREE.Vector3[] = [];
      for (let a = HALO_INI; a <= HALO_FIM; a += 3) arco.push(pontoHalo(centro, a).add(V(0, -90, 0)));
      grupo.add(new THREE.Line(new THREE.BufferGeometry().setFromPoints(arco), new THREE.LineBasicMaterial({ color: ciano, transparent: true, opacity: 0.28 })));
      const nu: Nucleo = { codigo: c.municipio.codigo, i, centro, grupo, anel, pulsos: [], feixes: [], giro: i * 9, comprimento: 1 };
      novos.push(novoSlot(m, `cidade-${c.municipio.codigo}`, "cidade", c.municipio.codigo, centro.clone().add(V(0, 40, 60)), c.municipio.codigo));
      const fila = c.setores.filter((s) => anomalo(s.nota)), normais = c.setores.filter((s) => !anomalo(s.nota));
      fila.forEach((s, j) => {
        const p = centro.clone().add(V((j - (fila.length - 1) / 2) * 232, -205, 150));
        novos.push(novoSlot(m, `fila-${s.codigo}`, "fila", c.municipio.codigo, p, s.codigo));
        const cor = cores[s.nota.nivel];
        const feixe = new THREE.Mesh(new THREE.CylinderGeometry(3, 3, p.y - 75 + 340, 8, 1, true),
          new THREE.MeshBasicMaterial({ color: cor, transparent: true, opacity: 0.7, blending: THREE.AdditiveBlending, depthWrite: false }));
        feixe.position.set(p.x, (p.y - 75 - 340) / 2, p.z);
        grupo.add(feixe);
        const pulso = new THREE.Mesh(new THREE.RingGeometry(30, 36, 48),
          new THREE.MeshBasicMaterial({ color: cor, transparent: true, opacity: 0.8, side: THREE.DoubleSide, blending: THREE.AdditiveBlending, depthWrite: false }));
        pulso.rotation.x = -Math.PI / 2;
        pulso.position.set(p.x, -337, p.z);
        grupo.add(pulso);
        nu.pulsos.push(pulso);
        nu.feixes.push(feixe, pulso);
      });
      const voltas = normais.length ? Math.max(1, Math.ceil((HALO_FIM - HALO_INI + HALO_PASSO) / (normais.length * HALO_PASSO))) : 0;
      let k = 0;
      for (let v = 0; v < voltas; v++) normais.forEach((s) => { novos.push(novoSlot(m, `halo-${s.codigo}-${v}`, "halo", c.municipio.codigo, centro.clone(), s.codigo, v > 0, k++)); });
      nu.comprimento = Math.max(k * HALO_PASSO, HALO_FIM - HALO_INI + HALO_PASSO);
      m.cena.add(grupo);
      m.nucleos.push(nu);
    });
    m.slots = novos;
    setSlots(novos);
    if (!m.vista) { const v = vistaGeral(); m.pos.copy(v.pos); m.alvo.copy(v.alvo); m.sph.setFromVector3(m.pos.clone().sub(m.alvo)); m.sphAlvo.copy(m.sph); m.vista = v; }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assinatura, vistaGeral]);

  // ------------------------------------------------------------------ níveis
  const equipsDoSetor = setor ? (modelo.setor(setor)?.equipamentos.map((e) => e.device).join(",") ?? "") : "";
  useEffect(() => {
    const m = motor.current;
    if (!m) return;
    // extras do nível anterior saem
    m.extras.forEach((s) => { m.cenaFrente.remove(s.obj); s.el.remove(); });
    m.linhas.forEach((l) => { m.cena.remove(l); liberar(l); });
    m.extras = []; m.linhas = [];
    const novosExtras: Slot[] = [];
    const extra = (chave: string, tipo: Tipo, pos: THREE.Vector3, ref: string, atraso = 0) => {
      const s = novoSlot(m, chave, tipo, cidade ?? "", pos, ref);
      s.entrada = -atraso * 2.2;
      novosExtras.push(s);
      return s;
    };
    const linha = (a: THREE.Vector3, b: THREE.Vector3, cor: THREE.Color) => {
      const l = new THREE.Line(new THREE.BufferGeometry().setFromPoints([a, b]), new THREE.LineBasicMaterial({ color: cor, transparent: true, opacity: 0.55 }));
      m.cena.add(l);
      m.linhas.push(l);
    };
    const nu = nivel >= 1 ? m.nucleos.find((x) => x.codigo === cidade) : undefined;
    for (const s of m.slots) {
      s.fixo = null;
      const viva = !nu || s.cidade === cidade;
      s.alvoOpacidade = !viva ? 0 : nivel >= 2 && s.ref !== setor ? (s.tipo === "cidade" ? 0.25 : 0) : 1;
    }
    for (const x of m.nucleos) { x.grupo.visible = !nu || x === nu; x.feixes.forEach((f) => { f.visible = nivel === 0; }); }
    // o card em foco passa para a camada da frente (nada o cobre); de volta ao halo, para a de trás
    const camada = (s: Slot, frente: boolean) => { if (s.tipo === "halo") (frente ? m.cenaFrente : m.cenaFundo).add(s.obj); };
    m.slots.forEach((s) => camada(s, false));
    if (!nu) { setExtras([]); const v = vistaGeral(); voar(v.pos, v.alvo); return; }
    const base = nu.centro;
    const cores = coresNivel();
    // cidade aberta: cada setor (uma cópia só) vem para a frente, em grade de leitura abaixo do card da cidade
    const vistos = new Set<string>();
    const unicos = m.slots.filter((s) => s.cidade === cidade && (s.tipo === "fila" || s.tipo === "halo")).filter((s) => {
      if (vistos.has(s.ref)) { s.alvoOpacidade = 0; return false; }
      vistos.add(s.ref);
      return true;
    });
    const ordem = new Map(modeloRef.current.cidade(cidade)?.setores.map((s, j) => [s.codigo, j]) ?? []);
    unicos.sort((a, b) => (ordem.get(a.ref) ?? 0) - (ordem.get(b.ref) ?? 0));
    const cols = unicos.length > 8 ? 6 : Math.min(4, Math.max(1, unicos.length));
    const linhasSetores = Math.ceil(unicos.length / cols);
    unicos.forEach((s, j) => { s.fixo = base.clone().add(V(((j % cols) - (cols - 1) / 2) * 236, -175 - Math.floor(j / cols) * 200, 300)); camada(s, true); });
    if (nivel === 1) {
      for (let j = 0; j < 6; j++) extra(`bloco-${j}`, "bloco", base.clone().add(V((j - 2.5) * 222, 300, 40)), String(j), j * 0.06);
      const yTend = -175 - linhasSetores * 200 - 40;
      for (let j = 0; j < 4; j++) extra(`tend-${j}`, "tend", base.clone().add(V((j - 1.5) * 255, yTend, 260)), String(j), 0.3 + j * 0.06);
      m.extras = novosExtras;
      setExtras(novosExtras);
      const topo = 380, fundo = yTend - 60, meio = (topo + fundo) / 2;
      const dist = Math.max(1150, (topo - fundo) / 0.66 + 160);   // a barra e a trilha do sistema tiram altura do palco
      voar(base.clone().add(V(0, meio + 40, dist)), base.clone().add(V(0, meio, 0)));
      return;
    }
    // setor em foco: avança para a frente da cidade; os equipamentos se abrem a partir dele
    const objSetor = unicos.find((s) => s.ref === setor);
    const sp = base.clone().add(V(0, 150, 430));
    if (objSetor) { objSetor.fixo = sp.clone(); objSetor.alvoOpacidade = 1; }
    const eqs = modeloRef.current.setor(setor)?.equipamentos ?? [];
    const colsEq = eqs.length > 4 ? Math.ceil(eqs.length / 2) : Math.max(1, eqs.length);
    const posEq = (j: number) => sp.clone().add(V(((j % colsEq) - (colsEq - 1) / 2) * 232, -230 - Math.floor(j / colsEq) * 190, 160));
    eqs.forEach((e, j) => {
      const p = posEq(j);
      const s = extra(`equip-${e.device}`, "equip", p, e.device, j * 0.07);
      if (nivel >= 3) s.alvoOpacidade = e.device === equip ? 1 : 0;
      if (nivel === 2) linha(sp.clone().add(V(0, -80, 0)), p.clone().add(V(0, 80, 0)), e.nota.nivel === "normal" ? cores.normal : cores[e.nota.nivel]);
    });
    if (nivel === 2) {
      m.extras = novosExtras;
      setExtras(novosExtras);
      voar(sp.clone().add(V(0, -120, 1050)), sp.clone().add(V(0, -180, 0)));
      return;
    }
    const j = Math.max(0, eqs.findIndex((e) => e.device === equip));
    const ep = posEq(j);
    if (objSetor) objSetor.alvoOpacidade = 0.15;
    const lado = ep.clone().add(V(420, 0, 40));
    const painel = extra(`painel-${equip}`, "painel", lado, equip ?? "", 0.1);
    linha(ep.clone().add(V(106, 0, 0)), lado.clone().add(V(-280, 0, 0)), cores.normal);
    if (nivel === 3) {
      m.extras = novosExtras;
      setExtras(novosExtras);
      voar(ep.clone().add(V(300, 0, 860)), ep.clone().add(V(300, 0, 0)));
      return;
    }
    painel.alvoOpacidade = 0.3;
    const baixo = lado.clone().add(V(0, -470, 60));
    extra(`diag-${equip}`, "diag", baixo, equip ?? "", 0.1);
    linha(lado.clone().add(V(0, -200, 0)), baixo.clone().add(V(0, 160, 0)), cores.normal);
    m.extras = novosExtras;
    setExtras(novosExtras);
    voar(baixo.clone().add(V(-60, 60, 820)), baixo.clone().add(V(-60, 40, 0)));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nivel, cidade, setor, equip, slots, equipsDoSetor, voar, vistaGeral]);

  // ------------------------------------------------------------------ câmera: arrastar, roda fora dos cards, setas, ＋ −
  useEffect(() => {
    const el = palco.current!;
    let ini: { x: number; y: number; moveu: boolean } | null = null;
    const baixo = (ev: PointerEvent) => {
      if (ev.button !== 0 || (ev.target as HTMLElement).closest("[data-zoom], .slot-3d, .hud, .camera-3d, button, a")) return;
      ini = { x: ev.clientX, y: ev.clientY, moveu: false };
    };
    const mover = (ev: PointerEvent) => {
      const m = motor.current;
      if (!ini || !m || m.voo) return;
      const dx = ev.clientX - ini.x, dy = ev.clientY - ini.y;
      if (!ini.moveu && Math.hypot(dx, dy) < 5) return;
      ini.moveu = true;
      el.classList.add("arrastando");
      m.sphAlvo.theta -= dx * 0.005;
      m.sphAlvo.phi = THREE.MathUtils.clamp(m.sphAlvo.phi + dy * 0.004, FI_MIN, FI_MAX);
      ini.x = ev.clientX; ini.y = ev.clientY;
    };
    const soltar = () => {
      const m = motor.current;
      if (ini?.moveu && m) { m.arrastou = true; window.setTimeout(() => { if (motor.current) motor.current.arrastou = false; }, 60); }
      ini = null;
      el.classList.remove("arrastando");
    };
    const roda = (ev: WheelEvent) => {
      const m = motor.current;
      if (!m || (ev.target as HTMLElement).closest("[data-zoom], .hud")) return;   // sobre um card: zoom de nível (CcoNovo)
      ev.preventDefault();
      m.sphAlvo.radius = THREE.MathUtils.clamp(m.sphAlvo.radius * (1 + Math.sign(ev.deltaY) * Math.min(0.25, Math.abs(ev.deltaY) * 0.0015)), RAIO_MIN, RAIO_MAX);
    };
    const clique = (ev: MouseEvent) => { if (motor.current?.arrastou) { ev.preventDefault(); ev.stopPropagation(); } };
    const tecla = (ev: KeyboardEvent) => {
      const m = motor.current;
      if (!m || m.voo || (ev.target as HTMLElement)?.matches?.("input, textarea, select")) return;
      const giro: Record<string, [number, number]> = { ArrowLeft: [-0.12, 0], ArrowRight: [0.12, 0], ArrowUp: [0, 0.08], ArrowDown: [0, -0.08] };
      if (giro[ev.key]) { ev.preventDefault(); m.sphAlvo.theta -= giro[ev.key][0]; m.sphAlvo.phi = THREE.MathUtils.clamp(m.sphAlvo.phi - giro[ev.key][1], FI_MIN, FI_MAX); }
      if (ev.key === "+" || ev.key === "=") m.sphAlvo.radius = Math.max(RAIO_MIN, m.sphAlvo.radius * 0.85);
      if (ev.key === "-") m.sphAlvo.radius = Math.min(RAIO_MAX, m.sphAlvo.radius * 1.18);
    };
    el.addEventListener("pointerdown", baixo);
    window.addEventListener("pointermove", mover);
    window.addEventListener("pointerup", soltar);
    el.addEventListener("wheel", roda, { passive: false });
    el.addEventListener("click", clique, true);
    window.addEventListener("keydown", tecla);
    return () => {
      el.removeEventListener("pointerdown", baixo);
      window.removeEventListener("pointermove", mover);
      window.removeEventListener("pointerup", soltar);
      el.removeEventListener("wheel", roda);
      el.removeEventListener("click", clique, true);
      window.removeEventListener("keydown", tecla);
    };
  }, []);

  const camera = (cmd: "mais" | "menos" | "centro") => {
    const m = motor.current;
    if (!m) return;
    if (cmd === "centro" && m.vista) { voar(m.vista.pos.clone(), m.vista.alvo.clone()); return; }
    m.sphAlvo.radius = THREE.MathUtils.clamp(m.sphAlvo.radius * (cmd === "mais" ? 0.8 : 1.25), RAIO_MIN, RAIO_MAX);
  };

  const cidadeFoco = modelo.cidade(cidade);
  const blocos = cidadeFoco ? blocosCidade(cidadeFoco) : [];
  const tends = blocosTendencia(tendencias);
  const conteudo = (s: Slot): ReactNode => {
    if (s.tipo === "cidade") { const c = modelo.cidade(s.ref); return c ? <CardCidade c={c} /> : null; }
    if (s.tipo === "fila" || s.tipo === "halo") { const st = modelo.setor(s.ref); return st ? <CardSetor s={st} modelo={modelo} copia={s.copia} /> : null; }
    if (s.tipo === "bloco") return <div className="b3d">{blocos[Number(s.ref)]}</div>;
    if (s.tipo === "tend") return <div className="t3d">{tends[Number(s.ref)]}</div>;
    const e = modelo.equipamento(s.ref);
    if (!e) return null;
    if (s.tipo === "equip") return <CardEquip e={e} modelo={modelo} />;
    if (s.tipo === "painel") return <PainelEquip e={e} modelo={modelo} nos3d />;
    return <div className="painel p3d"><CorpoDiag e={e} modelo={modelo} /></div>;
  };
  return (
    <div ref={palco} className="plano3d" aria-label="Sala de controle 3D">
      {nivel === 0 && <Hud ind={modelo.cliente} titulo="Indicadores do cliente" />}
      <div className="camera-3d" role="group" aria-label="Câmera 3D">
        <button type="button" aria-label="Aproximar" onClick={() => camera("mais")}>＋</button>
        <button type="button" aria-label="Afastar" onClick={() => camera("menos")}>−</button>
        <button type="button" aria-label="Centralizar a vista" onClick={() => camera("centro")}>⟲</button>
      </div>
      <span className="aviso-3d">motor 3D do SCADA (three.js) · arraste para girar · roda fora dos cards aproxima e afasta · roda sobre um card abre o nível · Esc volta</span>
      {[...slots, ...extras].map((s) => createPortal(conteudo(s), s.el, s.chave))}
    </div>
  );
}
